import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getSchemeBuckets, SchemeBucket } from '../../services/schemeBucket';

interface SchemeBucketFilters { }

interface SchemeBucketState {
  buckets: SchemeBucket[];
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  searchQuery: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  error?: string;
  hasFetched: boolean; // track if already fetched
}

const initialState: SchemeBucketState = {
  buckets: [],
  total: 0,
  loading: false,
  page: 1,
  pageSize: 10,
  searchQuery: '',
  hasFetched: false,
  sortBy: "createdAt",
  sortOrder: "desc",
};

// Thunk to fetch buckets
export const fetchBucketsThunk = createAsyncThunk<
  { data: SchemeBucket[]; total: number },
  void,
  { state: { schemeBucket: SchemeBucketState } }
>('schemeBucket/fetchBuckets', async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const { page, pageSize, searchQuery, sortBy,
      sortOrder } = state.schemeBucket;

    const response = await getSchemeBuckets({
      page,
      pageSize,
      search: searchQuery,
      sortBy,
      sortOrder,
    });

    return {
      data: response.data || [],
      total: response.pagination?.total || 0,
    };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

const schemeBucketSlice = createSlice({
  name: 'schemeBucket',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setHasFetched: (state, action: PayloadAction<boolean>) => {
      state.hasFetched = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBucketsThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchBucketsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.buckets = action.payload.data;
        state.total = action.payload.total;
        state.hasFetched = true;
      })
      .addCase(fetchBucketsThunk.rejected, (state, action) => {
        state.loading = false;
        state.buckets = [];
        state.total = 0;
        state.error = action.payload as string;
      });
  },
});

export const { setPage, setPageSize, setSearchQuery, setHasFetched, setSortBy, setSortOrder } = schemeBucketSlice.actions;

export default schemeBucketSlice.reducer;
